import React, { useState } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Heart,
  Flag,
  RefreshCw,
  ArrowLeft,
  Clock,
  MessageSquare,
  User,
  AlertCircle
} from 'lucide-react';
import { api } from '../services/api';
import type { Prayer } from '../services/api';

interface ReceivePrayerState {
  prayer: Prayer | null;
  loading: boolean;
  error: string | null;
  praying: boolean;
  showThankYou: boolean;
}

const ReceivePrayer: React.FC = () => {
  const [state, setState] = useState<ReceivePrayerState>({
    prayer: null,
    loading: false,
    error: null,
    praying: false,
    showThankYou: false
  });

  const fetchPrayer = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.prayers.receive();
      setState(prev => ({ 
        ...prev, 
        prayer: response.data,
        praying: false,
        showThankYou: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'No prayer requests available at this time. Please check back later.',
        prayer: null
      }));
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleReport = async () => {
    if (!state.prayer) return;
    
    try {
      await api.prayers.report(state.prayer._id);
      setState(prev => ({
        ...prev,
        prayer: null,
        error: 'Prayer has been reported. Thank you for helping maintain our community guidelines.'
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to report prayer. Please try again.'
      }));
    }
  };

  const handlePrayedFor = () => {
    setState(prev => ({ ...prev, praying: true }));
    setTimeout(() => {
      setState(prev => ({ ...prev, showThankYou: true }));
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#171720] text-white p-6">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            Receive a Prayer Request
          </h1>
          <p className="text-gray-400">
            Join in prayer for someone in need of spiritual support
          </p>
        </div>

        {state.loading ? (
          <Card className="bg-[#23232F] border-[#2D2D39]">
            <CardContent className="py-16 flex flex-col items-center">
              <RefreshCw className="w-8 h-8 animate-spin text-[#FF8A4C] mb-4" />
              <p className="text-gray-400">Finding a prayer request...</p>
            </CardContent>
          </Card>
        ) : state.error ? (
          <Card className="bg-[#23232F] border-[#2D2D39]">
            <CardContent className="p-8">
              <Alert className="mb-6 bg-red-900/20 border-red-900/50 text-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{state.error}</AlertDescription>
              </Alert>
              <Button 
                onClick={fetchPrayer} 
                className="w-full rounded-xl bg-[#FF8A4C] hover:bg-[#FF7A4C] border-0"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : state.prayer ? (
          <Card className="bg-[#23232F] border-[#2D2D39]">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <Badge className="bg-[#2D2D39] text-[#FF8A4C] mb-2">
                    Prayer Request
                  </Badge>
                  <div className="flex items-center text-sm text-gray-400">
                    <User className="w-4 h-4 mr-1" />
                    {state.prayer.senderName || 'Anonymous'}
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    {new Date(state.prayer.createdAt).toLocaleString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReport}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Flag className="w-4 h-4" />
                </Button>
              </div>

              <div className="bg-[#1C1C24] rounded-xl p-6 mb-6">
                <MessageSquare className="w-6 h-6 text-[#FF8A4C] mb-3" />
                <p className="text-gray-200 text-lg leading-relaxed">
                  {state.prayer.content}
                </p>
              </div>

              {!state.praying ? (
                <Button 
                  onClick={handlePrayedFor} 
                  className="w-full rounded-xl bg-[#FF8A4C] hover:bg-[#FF7A4C] 
                           border-0 h-14 text-lg"
                >
                  I'll Pray for This
                </Button>
              ) : state.showThankYou ? (
                <div className="text-center space-y-4">
                  <div className="py-4">
                    <Heart className="w-12 h-12 text-[#FF8A4C] mx-auto mb-2" />
                    <p className="text-gray-200 font-medium">
                      Thank you for praying!
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <Button 
                      onClick={() => setState(prev => ({ ...prev, prayer: null }))} 
                      variant="outline"
                      className="flex-1 rounded-xl bg-[#2D2D39] hover:bg-[#34343F] 
                               border-[#2D2D39] text-white"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      onClick={fetchPrayer}
                      className="flex-1 rounded-xl bg-[#FF8A4C] hover:bg-[#FF7A4C] border-0"
                    >
                      Pray for Another
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <Clock className="w-6 h-6 animate-pulse text-[#FF8A4C] mr-2" />
                  <span className="text-gray-400">Praying...</span>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#23232F] border-[#2D2D39]">
            <CardContent className="py-16 text-center">
              <MessageSquare className="w-16 h-16 text-[#FF8A4C] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-4">
                Ready to Support Others in Prayer?
              </h2>
              <p className="text-gray-400 mb-8">
                Click below to receive a prayer request from someone in need
              </p>
              <Button 
                onClick={fetchPrayer}
                className="rounded-xl bg-[#FF8A4C] hover:bg-[#FF7A4C] 
                         border-0 px-8 py-6 text-lg"
              >
                Receive a Prayer Request
              </Button>
            </CardContent>
          </Card>
        )}

        {!state.loading && !state.error && !state.prayer && (
          <div className="mt-6">
            <Button
              onClick={() => window.history.back()}
              variant="outline"
              className="w-full rounded-xl bg-[#2D2D39] hover:bg-[#34343F] 
                       border-[#2D2D39] text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivePrayer;